/**
 * Kingdom Minded — Volumes accordion.
 *
 * The markup is native <details>/<summary>, so it is fully usable with JS disabled.
 * This only adds three refinements:
 *   1. single-open behaviour (closing siblings when one opens)
 *   2. a height transition, since <details> cannot animate on its own
 *   3. an optional "open/close all" toggle driven by the section's header button
 *
 * The toggle is progressive enhancement. In the markup the button is a plain link
 * to the Volumes page; only when it carries data-km-vols-toggle does this script
 * intercept the click and expand the six panels in place instead of navigating.
 *
 * Respects prefers-reduced-motion by skipping the animation entirely.
 */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DURATION = 380;

  function animateOpen(panel) {
    if (reduceMotion) return;
    panel.style.height = '0px';
    panel.style.transition = `height ${DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
    });
    setTimeout(() => {
      panel.style.height = '';
      panel.style.transition = '';
    }, DURATION);
  }

  function animateClose(details, panel, done) {
    if (reduceMotion) {
      done();
      return;
    }
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.transition = `height ${DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });
    setTimeout(() => {
      panel.style.height = '';
      panel.style.transition = '';
      done();
    }, DURATION);
  }

  function init(list) {
    // Guard against double-binding when a section is re-rendered in the editor.
    if (list.dataset.kmVolumesBound === 'true') return;
    list.dataset.kmVolumesBound = 'true';

    const singleOpen = list.dataset.singleOpen !== 'false';
    const items = Array.from(list.querySelectorAll('.km-vol'));
    if (!items.length) return;

    const section = list.closest('.km-vols') || document;
    const toggle = section.querySelector('[data-km-vols-toggle]');

    // While every panel is open, single-open would slam five of them shut the
    // moment the customer touched a sixth. Expanding all suspends it; collapsing
    // back to none restores it.
    let suspendSingleOpen = false;

    const openCount = () => items.filter((d) => d.open).length;

    function syncToggle() {
      if (!toggle) return;
      const all = openCount() === items.length;
      toggle.textContent = all
        ? toggle.dataset.labelClose || 'Close Volumes'
        : toggle.dataset.labelOpen || 'Open Volumes';
      toggle.setAttribute('aria-expanded', all ? 'true' : 'false');
    }

    function openAll() {
      suspendSingleOpen = true;
      items.forEach((details) => {
        if (details.open) return;
        const panel = details.querySelector('.km-vol__panel');
        details.open = true;
        if (panel) animateOpen(panel);
      });
      syncToggle();
    }

    function closeAll() {
      items.forEach((details) => {
        if (!details.open) return;
        const panel = details.querySelector('.km-vol__panel');
        if (!panel) {
          details.open = false;
          return;
        }
        animateClose(details, panel, () => {
          details.open = false;
          syncToggle();
        });
      });
      suspendSingleOpen = false;
      syncToggle();
    }

    items.forEach((details) => {
      const summary = details.querySelector('.km-vol__summary');
      const panel = details.querySelector('.km-vol__panel');
      if (!summary || !panel) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();

        // Closing the open item.
        if (details.open) {
          animateClose(details, panel, () => {
            details.open = false;
            if (openCount() === 0) suspendSingleOpen = false;
            syncToggle();
          });
          return;
        }

        if (singleOpen && !suspendSingleOpen) {
          items.forEach((other) => {
            if (other === details || !other.open) return;
            const otherPanel = other.querySelector('.km-vol__panel');
            animateClose(other, otherPanel, () => {
              other.open = false;
              syncToggle();
            });
          });
        }

        details.open = true;
        animateOpen(panel);
        syncToggle();
      });
    });

    // Arriving from the header's Volumes dropdown as /pages/volumes#vol-iii:
    // open that volume so the customer lands on its content rather than a
    // closed row. Done without animation - km-anchor-jump.js measures the
    // scroll position right after, and must see the final height.
    if (location.hash) {
      const target = items.find((d) => d.id && '#' + d.id === location.hash);
      if (target && !target.open) {
        target.open = true;
        suspendSingleOpen = false;
      }
    }

    if (toggle) {
      if (!list.id) list.id = `km-vols-list-${Math.random().toString(36).slice(2, 8)}`;
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('aria-controls', list.id);
      toggle.addEventListener('click', (e) => {
        // Only now does the link stop being a link.
        e.preventDefault();
        if (openCount() === items.length) {
          closeAll();
        } else {
          openAll();
        }
      });
      syncToggle();
    }
  }

  function boot() {
    document.querySelectorAll('[data-km-volumes]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-km-volumes]').forEach(init);
  });
})();
